import { ref, computed } from 'vue'
const $str = window.$str

export function useList(fn, immediate = true, dataProcess) {
  const pageNum = ref(1)
  const pageSize = ref(10)
  const list = ref([])
  const total = ref(0)
  const loading = ref(false)

  const fetchList = () => {
    loading.value = true
    const param = { pageNum: pageNum.value, pageSize: pageSize.value }

    // 返回 Promise,便于调用方在请求真正结束后再做后续处理(如轮询排期)
    return fn(param)
      .then(res => {
        dataProcess && dataProcess(res.rows)
        list.value = res.rows
        total.value = res.total
      })
      .catch(err => console.warn(err))
      .finally(() => loading.value = false)
  }

  if (immediate) {
    fetchList()
  }

  return {
    pageNum,
    pageSize,
    list,
    total,
    loading,
    fetchList
  }
}

export function translateUpgradeState(s) {
  const table = {
    start_upgrade: $str.getKey('设备升级/命令已下发'),
    req_recv: $str.getKey('设备升级/收到请求'),
    downloading: $str.getKey('设备升级/下载中'),
    prep_upgrade: $str.getKey('设备升级/准备升级'),
    upgrading: $str.getKey('设备升级/正在升级'),
    verifying: $str.getKey('设备升级/版本校验中'),
    transfering: $str.getKey('设备升级/数据传输中'),
    wait_result: $str.getKey('设备升级/等待结果'),
    upgrade_success: $str.getKey('设备升级/升级成功'),
    upgrade_fail: $str.getKey('设备升级/升级失败')
  }

  return table[s] ?? $str.getKey('设备升级/未知')
}

export function getUpgradePercentage(s) {
  // 百分比为前端按状态推算（后端不上报进度），需与 translateUpgradeState 的状态保持一一对应，
  // 且数值随升级流程单调递增，否则进度条会出现回退
  const table = {
    start_upgrade: 0,
    req_recv: 0.05,
    downloading: 0.15,
    prep_upgrade: 0.35,
    transfering: 0.55,
    upgrading: 0.7,
    verifying: 0.85,
    wait_result: 0.95,
    upgrade_success: 1,
    upgrade_fail: 1,
  }

  // 未知状态兜底为 0，避免 undefined * 100 得到 NaN%
  const num = table[s] ?? 0

  return {
    str: num,
    percentage: Number((num * 100).toFixed(2))
  }
}

export function translateCode(code) {
  const table = {
    "100": $str.getKey('设备升级/升级成功'),
    "101": $str.getKey('设备升级/升级失败'),
    "102": $str.getKey('设备升级/升级包校验失败'),
    "103": $str.getKey('设备升级/升级前查询运行版本失败'),
    "104": $str.getKey('设备升级/升级后查询运行版本失败'),
  }
  return table[code]
}

export function getReason(record) {
  // resultMsg 为后端下发的原始描述，可能为空；为空时回退到结果码对应的文案
  const msg = record.resultMsg || translateCode(record.resultCode) || ''
  if (record.resultCode == '101') {
    const failed = record.resultFailed
    // resultFailed 为失败设备序号，可能不存在，不能无条件拼接
    if (failed !== undefined && failed !== null && failed !== '') {
      return msg + '。' + $str.getKey('设备升级/升级失败设备序号') + ':' + failed
    }
  }
  return msg
}

export function getUpgradeType(s) {
  const map = new Map()

  // 进行中的状态需与 translateUpgradeState 保持一致，
  // 漏掉的状态会返回 null 使 el-tag 落到默认样式
  map.set('', ['start_upgrade', 'req_recv', 'downloading', 'prep_upgrade', 'transfering', 'upgrading', 'verifying', 'wait_result'])
  map.set('success', ['upgrade_success'])
  map.set('danger', ['upgrade_fail'])

  for (const [key, value] of map) {
    if (value.includes(s)) {
      return key
    }
  }

  return null
}

export function getFileTagType(fileType) {
  // 需覆盖 pkg.vue 中允许上传的全部类型，否则该类型的标签没有颜色
  const TagTypes = {
    'BCS': 'success',
    'PCS': '',
    'BMU': 'danger',
    'ELINK': 'warning',
    'ELINK_KEY': 'info',
    'EMS': 'primary',
    'WATER': 'info',
    'FIRE': 'danger',
  }
  return TagTypes[fileType] ?? ''
}

export function createMergeRows(dataListRef, key) {
  return computed(() => {
    const mergingRows = []
    let mergingPos = 0
    for (let i = 0; i < dataListRef.value.length; i++) {
      if (i === 0) {
        mergingRows.push(1)
        mergingPos = 0
      } else {
        if (dataListRef.value[i][key] === dataListRef.value[i - 1][key]) {
          mergingRows[mergingPos] += 1
          mergingRows.push(0)
        } else {
          mergingRows.push(1)
          mergingPos = i
        }
      }
    }
    return mergingRows
  })
}

/**
 * 将不同类型的版本信息分组
 * @param {any[]} [versionList=[]]
 * @param {string[]} [comparekeys=[]]
 */
export function groupDifferenceVersions(versionList = [], comparekeys = []) {
  const group = new Map()
  let color

  for (let i = 0; i < versionList.length; i++) {
    const item = Object.assign({ $i: i + 1 }, versionList[i])
    const key = comparekeys.map(k => item[k]).join('=')
    if (group.has(key)) {
      group.get(key).push(item)
    } else {
      color = randomColor()
      group.set(key, [item])
    }
    item.$color = color
  }
  return group
}

export function randomColor(colors = []) {
  function getRandomInt() {
    return Math.floor(Math.random() * 256)
  }

  function componentToHex(c) {
    var hex = c.toString(16)
    return hex.length == 1 ? "0" + hex : hex
  }

  if (colors.length > 0) {
    const randomIndex = Math.floor(Math.random() * colors.length)
    return colors[randomIndex]
  }

  var r = componentToHex(getRandomInt())
  var g = componentToHex(getRandomInt())
  var b = componentToHex(getRandomInt())

  return "#" + r + g + b
}

/**
 * 极简 sessionStorage/localStorage 封装（替代源项目 @/utils createStorage）
 * @param {'sessionStorage'|'localStorage'} type
 */
export function createStorage(type = 'sessionStorage') {
  const store = type === 'localStorage' ? window.localStorage : window.sessionStorage
  return {
    save(key, value) {
      try {
        store.setItem(key, JSON.stringify(value))
      } catch (e) {
        console.warn(e)
      }
    },
    load(key, defaultValue) {
      try {
        const raw = store.getItem(key)
        return raw == null ? defaultValue : JSON.parse(raw)
      } catch (e) {
        return defaultValue
      }
    }
  }
}
